import React, { FC, useEffect, useState } from "react";
import { RootStateOrAny } from "react-redux";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Row,
  Select,
  Tooltip,
  Table,
  Divider,
  TimePicker,
  Input,
} from "antd";
import moment, { Moment } from "moment";
import { isEmpty, uniqueId } from "lodash";
import { displayFormat, toDisplayFormat, toHttpFormat } from "../../lib/utils/formatDate";
import "./AccountAccumulators.less";
import { checkIsLoaded, checkIsLoading } from "../../lib/utils/checkLoadingStatus";
import {
  GetServiceTooltipTitle,
  OnChangeFieldValue,
  GetAccumulators,
  AccumulatorsTableRecord,
  HandleExportAccumulators,
  LoadServices,
  Accumulator,
} from "../../lib/interfaces/Accumulators";
import { searchIconForInput } from "../../lib/icons";
import ToggleColumns from "../ToggleableVersionsTable/ToggleColumns";
import { AccumulatorsState } from "../../store/slices/accumulators";
import { TableColumn } from "../../lib/interfaces/TableRecord";
import { hasAccess } from "../RolesModule/hasAccess";
import { MENU_ACCOUNT_AGG_USAGE_WRITE } from "../../lib/const/permissions";
import { limitText } from "../../lib/utils/limitText";
import { useAppSelector } from "../..";

interface IProps {
  getServiceTooltipTitle: GetServiceTooltipTitle;
  loadServices: LoadServices;
  searchForm: any;
  getAccumulators: GetAccumulators;
  handleExport: HandleExportAccumulators;
}

const AccountAccumulators = ({
  getServiceTooltipTitle,
  loadServices,
  searchForm,
  getAccumulators,
  handleExport,
}: IProps) => {
  const { agreements } = useAppSelector((state: RootStateOrAny) => state.agreements);
  const { accountVersions } = useAppSelector((state) => state.accountsTs.accountListRowData);
  const { accumulators, accumulatorsLoading } = useAppSelector((state) => state.accumulators);
  const { service: services, serviceLoading } = useAppSelector(
    (state: RootStateOrAny) => state.service
  );
  const transactionalServices = services.filter(
    ({ offering }) =>
      Array.isArray(offering?.aggregationRules) && offering?.aggregationRules?.length > 0
  );

  console.log({ transactionalServices });

  const [accumulatorsFilterValues, setAccumulatorsFilterValues] = useState<{
    activeServiceIds: number[];
    activeOfferingIdIds: number[];
    activeServiceAggRuleIds: number[];
    activeIds: number[];
  }>({
    activeServiceIds: [],
    activeOfferingIdIds: [],
    activeServiceAggRuleIds: [],
    activeIds: [],
  });
  const [filterAccumulatorsForm] = Form.useForm();

  const {
    tariffModelTypes,
    periodTypes,
    serviceUsageDirection,
    serviceUsageDocumentType,
    tariffPeriodAlignments,
    retailNetworks,
  } = useAppSelector((state: RootStateOrAny) => state.rtkDictionaries);
  const { exportToXLSLoading } = useAppSelector((state) => state.xlsExport);

  const [tableData, setTableData] = useState<AccumulatorsTableRecord[]>([]);

  // Формирование массива значений для отображения Сеть (includeList) из ruleCondition в aggregationRule
  const getIncludeLists = (ruleCondition): { nameTitle: string; displayName: string }[] => {
    if (isEmpty(ruleCondition)) return [];
    return ruleCondition.map((ruleConditionItem) => {
      const { retailNetworksDic, retailNetworksUniqueIdDic } = retailNetworks;
      const selectedNetworkNames: any[] = [];
      const selectedNetworkValues: any[] = [];

      if (!isEmpty(ruleConditionItem?.includeList)) {
        ruleConditionItem.includeList.forEach((recObjectId) => {
          const selectedNetwork = retailNetworksUniqueIdDic.find(
            ({ objectId }) => String(objectId) === String(recObjectId)
          );
          const selectedNetworkName = selectedNetwork
            ? selectedNetwork?.stringValue
            : "!Сеть неизвестна!";
          if (!selectedNetworkNames.includes(selectedNetworkName)) {
            selectedNetworkNames.push(selectedNetworkName);
          }

          const selectedNetworkValue = retailNetworksDic.find(
            ({ stringValue }) => stringValue === selectedNetworkName
          )?.objectId;
          if (!selectedNetworkValues.includes(selectedNetworkValue)) {
            selectedNetworkValues.push(selectedNetworkValue);
          }
        });
      }

      let displayName = "";
      let nameTitle = "";

      if (!isEmpty(selectedNetworkNames)) {
        if (selectedNetworkNames.length > 1) {
          displayName = `${selectedNetworkNames[0]}...`;
          nameTitle = selectedNetworkNames.join("\n");
        } else {
          [displayName] = selectedNetworkNames;
          [nameTitle] = selectedNetworkNames;
        }
      }
      return { nameTitle, displayName };
    });
  };

  // Формирование значений для отображения Тип документа (documentTypeList) из ruleCondition в aggregationRule
  const getDocumentTypeLists = (ruleCondition): { nameTitle: string; displayName: string }[] => {
    if (isEmpty(ruleCondition)) return [];
    return ruleCondition.map((ruleConditionItem) => {
      const documentNamesList: any[] = [];

      if (!isEmpty(ruleConditionItem?.documentTypeList)) {
        ruleConditionItem.documentTypeList.forEach((recId) => {
          const docType = serviceUsageDocumentType.find(({ id }) => String(id) === String(recId));
          if (docType) {
            documentNamesList.push(docType.name);
          }
        });
      }

      let displayName = "";
      let nameTitle = "";

      if (!isEmpty(documentNamesList)) {
        if (documentNamesList.length > 1) {
          displayName = `${documentNamesList[0]}...`;
          nameTitle = documentNamesList.join("\n");
        } else {
          [displayName] = documentNamesList;
          [nameTitle] = documentNamesList;
        }
      }
      return { nameTitle, displayName };
    });
  };

  // Формирование значений для отображения Направление (direction) из ruleCondition в aggregationRule
  const getDirections = (ruleCondition): string[] => {
    if (isEmpty(ruleCondition)) return [];
    return ruleCondition.map((ruleConditionItem) => {
      const displayName =
        serviceUsageDirection.find(({ alias }) => {
          if (ruleConditionItem.direction === undefined) {
            return alias === "";
          }
          return alias === ruleConditionItem.direction;
        })?.name || "";
      return displayName;
    });
  };

  const getTableData = (allAccumulators: Accumulator[]): AccumulatorsTableRecord[] => {
    // Строки должны выделяться цветом для разных продуктов, для этого используется номер продукта по порядку
    const serviceCounter = { prevId: 0, sequenceNumber: 1 };
    return allAccumulators.map((accumulator) => {
      const service = transactionalServices.find((item) => item.id === accumulator.serviceId);

      if (serviceCounter.prevId && service.id !== serviceCounter.prevId) {
        serviceCounter.sequenceNumber += 1;
      }
      serviceCounter.prevId = service.id;

      const aggregationRule = service.offering?.aggregationRules.find(
        (aggRule) => aggRule.id === accumulator.serviceAggRuleId
      );

      const ruleCondition = aggregationRule && JSON.parse(aggregationRule.ruleCondition);

      // Фейковые данные для проверки формирования данных из ruleCondition
      // const ruleCondition = JSON.parse(
      //   '[{"isUniqRate":true,"includeList":[297435,297116,296947,298037],"documentTypeList":[3,6],"direction":"incoming"},{"relation":"OR","isUniqRate":true,"includeList":[297435,300873,297946],"documentTypeList":[3],"direction":"outgoing"}]'
      // );

      console.log({ service });

      return {
        agreementId: service?.agreement?.id,
        sequenceNumber: serviceCounter.sequenceNumber,
        ...accumulator,
        key: accumulator.id || uniqueId(),
        serviceTypeName: service?.serviceType.name,
        offeringName: service?.offering?.name,
        offeringId: service?.offering?.id,
        tariffModelName: tariffModelTypes.find(
          (tariffModelType) => tariffModelType.id === service?.offering?.tariffModelId
        )?.name,
        periodTypeName: periodTypes.find(
          (periodType) => periodType.id === service?.offering?.periodTypeId
        )?.name,
        periodAlignmentName: tariffPeriodAlignments.find(
          (tariffPeriodAlignment) =>
            tariffPeriodAlignment.id === service?.offering?.periodAlignmentId
        )?.name,
        servicePeriod: {
          fromDate: toDisplayFormat(service?.fromDate),
          toDate: toDisplayFormat(service?.toDate),
        },
        aggName: aggregationRule?.aggName,
        rangeFrom: aggregationRule?.rangeFrom,
        rangeTo: aggregationRule?.rangeTo,
        accumulationPeriod: {
          periodFrom: accumulator.periodFrom ? toDisplayFormat(accumulator.periodFrom) : "",
          periodTo: accumulator.periodTo ? toDisplayFormat(accumulator.periodTo) : "",
        },
        includeLists: getIncludeLists(ruleCondition),
        documentTypeLists: getDocumentTypeLists(ruleCondition),
        directions: getDirections(ruleCondition),
      };
    });
  };

  const sortAccumulators = (a, b) => {
    const serviceA = transactionalServices.find((item) => item.id === a.serviceId);
    const serviceB = transactionalServices.find((item) => item.id === b.serviceId);
    if (serviceA.id === serviceB.id) {
      const serviceAggRuleA = serviceA.offering?.aggregationRules.find(
        (aggRule) => aggRule.id === a.serviceAggRuleId
      );
      const serviceAggRuleB = serviceB.offering?.aggregationRules.find(
        (aggRule) => aggRule.id === b.serviceAggRuleId
      );
      return serviceAggRuleA.rangeFrom > serviceAggRuleB.rangeFrom ? 1 : -1;
    }
    return moment(serviceA.fromDate).isBefore(serviceB.fromDate) ? 1 : -1;
  };

  // Формирование данных таблицы
  useEffect(() => {
    // В случае если продукт в фильтре выбрана, остается только она, иначе - все транзакционные
    const filteredTransactionalServices = transactionalServices.filter(({ id }) => {
      const selectedServiceId = searchForm.getFieldValue("serviceId");
      if (selectedServiceId) {
        return id === selectedServiceId;
      }
      return true;
    });
    if (filteredTransactionalServices.length === 0) {
      setTableData([]);
    } else if (checkIsLoaded(accumulatorsLoading)) {
      // К аккумуляторам полученным добавляются пустые по тем aggregationRule, для которых не получены
      const allAccumulators: Accumulator[] = [];
      filteredTransactionalServices.forEach((serviceItem) => {
        if (Array.isArray(serviceItem.offering.aggregationRules)) {
          serviceItem.offering.aggregationRules.forEach((serviceItemAggRule) => {
            const gettedAcumulator = accumulators.find(
              ({ serviceId, serviceAggRuleId }) =>
                serviceId === serviceItem.id && serviceAggRuleId === serviceItemAggRule.id
            );
            const accum: Accumulator = gettedAcumulator || {
              id: 0,
              serviceId: serviceItem.id,
              serviceAggRuleId: serviceItemAggRule.id,
              periodFrom: "",
              periodTo: "",
              eventsCount: 0,
              rangeFrom: serviceItemAggRule.rangeFrom,
              rangeTo: serviceItemAggRule.rangeTo,
              eventsVolume: 0,
              info: null,
              tenantId: serviceItem.tenantId,
            };
            allAccumulators.push(accum);
          });
        }
      });
      allAccumulators.sort(sortAccumulators);

      setTableData(
        getTableData(allAccumulators).filter(({ serviceId, offeringId, serviceAggRuleId, id }) => {
          if (
            accumulatorsFilterValues.activeServiceIds.length === 0 &&
            accumulatorsFilterValues.activeOfferingIdIds.length === 0 &&
            accumulatorsFilterValues.activeServiceAggRuleIds.length === 0 &&
            accumulatorsFilterValues.activeIds.length === 0
          ) {
            return true;
          }
          return (
            accumulatorsFilterValues.activeServiceIds.some((activeId) =>
              String(serviceId).startsWith(String(activeId))
            ) ||
            accumulatorsFilterValues.activeOfferingIdIds.some((activeId) =>
              String(offeringId).startsWith(String(activeId))
            ) ||
            accumulatorsFilterValues.activeServiceAggRuleIds.some((activeId) =>
              String(serviceAggRuleId).startsWith(String(activeId))
            ) ||
            accumulatorsFilterValues.activeIds.some((activeId) =>
              String(id).startsWith(String(activeId))
            )
          );
        })
      );
    }
  }, [
    accumulators,
    accumulatorsLoading,
    tariffModelTypes,
    periodTypes,
    serviceUsageDirection,
    serviceUsageDocumentType,
    tariffPeriodAlignments,
    retailNetworks,
    accumulatorsFilterValues,
  ]);

  const renderTableHeaderFilters = (typeField: string) => {
    const notValidRgx = /[^0-9,]/;
    let valueTrim = "";
    const activeIdes = {};

    const onChangeId = (typeField: string, { target: { value } }) => {
      valueTrim = value.replace(/^\s+/g, "");
      activeIdes[typeField] = valueTrim?.split(/\D/).filter(Boolean).map(Number);

      if (!notValidRgx.test(value)) {
        if (typeField === "serviceId") {
          filterAccumulatorsForm.setFieldsValue({ serviceId: valueTrim });

          setAccumulatorsFilterValues((prevState) => ({
            ...prevState,
            activeServiceIds: activeIdes[typeField],
          }));
        }

        if (typeField === "offeringId") {
          filterAccumulatorsForm.setFieldsValue({ offeringId: valueTrim });

          setAccumulatorsFilterValues((prevState) => ({
            ...prevState,
            activeOfferingIdIds: activeIdes[typeField],
          }));
        }

        if (typeField === "serviceAggRuleId") {
          filterAccumulatorsForm.setFieldsValue({ serviceAggRuleId: valueTrim });

          setAccumulatorsFilterValues((prevState) => ({
            ...prevState,
            activeServiceAggRuleIds: activeIdes[typeField],
          }));
        }

        // if (typeField === "id") {
        //   filterAccumulatorsForm.setFieldsValue({ id: valueTrim });

        //   setAccumulatorsFilterValues((prevState) => ({
        //     ...prevState,
        //     activeIds: activeIdes[typeField],
        //   }));
        // }
      }
    };

    if (typeField === "serviceId")
      return (
        <Tooltip title="Введите ID продуктов через ','">
          <Form form={filterAccumulatorsForm}>
            <Form.Item
              name="serviceId"
              style={{ marginBottom: 0 }}
              className="form-item-required__hidden-error__mb0"
              rules={[
                {
                  validator: (x, value) => {
                    if (value && /[^0-9,]/.test(value)) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                aria-autocomplete="none"
                placeholder="ID продукта"
                className="table-head-filter-search"
                allowClear
                suffix={searchIconForInput()}
                onChange={(e) => onChangeId("serviceId", e)}
              />
            </Form.Item>
          </Form>
        </Tooltip>
      );

    if (typeField === "offeringId")
      return (
        <Tooltip title="Введите ID предложений через ','">
          <Form form={filterAccumulatorsForm}>
            <Form.Item
              name="offeringId"
              style={{ marginBottom: 0 }}
              className="form-item-required__hidden-error__mb0"
              rules={[
                {
                  validator: (x, value) => {
                    if (value && /[^0-9,]/.test(value)) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                aria-autocomplete="none"
                className="table-head-filter-search"
                placeholder="ID предложения"
                allowClear
                suffix={searchIconForInput()}
                onChange={(e) => onChangeId("offeringId", e)}
              />
            </Form.Item>
          </Form>
        </Tooltip>
      );

    if (typeField === "serviceAggRuleId")
      return (
        <Tooltip title="Введите ID группы через ','">
          <Form form={filterAccumulatorsForm}>
            <Form.Item
              name="serviceAggRuleId"
              style={{ marginBottom: 0 }}
              className="form-item-required__hidden-error__mb0"
              rules={[
                {
                  validator: (x, value) => {
                    if (value && /[^0-9,]/.test(value)) {
                      return Promise.reject();
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Input
                aria-autocomplete="none"
                className="table-head-filter-search"
                placeholder="ID группы"
                allowClear
                suffix={searchIconForInput()}
                onChange={(e) => onChangeId("serviceAggRuleId", e)}
              />
            </Form.Item>
          </Form>
        </Tooltip>
      );

    // if (typeField === "id")
    //   return (
    //     <Tooltip title="Введите ID аккум. через ','">
    //       <Form form={filterAccumulatorsForm}>
    //         <Form.Item
    //           name="id"
    //           style={{ marginBottom: 0 }}
    //           className="form-item-required__hidden-error__mb0"
    //           rules={[
    //             {
    //               validator: (x, value) => {
    //                 if (value && /[^0-9,]/.test(value)) {
    //                   return Promise.reject();
    //                 }
    //                 return Promise.resolve();
    //               },
    //             },
    //           ]}
    //         >
    //           <Input
    //             aria-autocomplete="none"
    //             className="table-head-filter-search"
    //             placeholder="ID аккум."
    //             allowClear
    //             suffix={searchIconForInput()}
    //             onChange={(e) => onChangeId("id", e)}
    //           />
    //         </Form.Item>
    //       </Form>
    //     </Tooltip>
    //   );

    return false;
  };

  const renderIdTitle = () => renderTableHeaderFilters("serviceId");

  if (renderIdTitle) {
    renderIdTitle.toString = () => "ID продукта";
  }

  const renderIdOffering = () => renderTableHeaderFilters("offeringId");

  if (renderIdOffering) {
    renderIdOffering.toString = () => "ID предложения";
  }

  const renderIdGroup = () => renderTableHeaderFilters("serviceAggRuleId");

  if (renderIdGroup) {
    renderIdGroup.toString = () => "ID группы";
  }

  // const renderIdAccum = () => renderTableHeaderFilters("id");

  // if (renderIdAccum) {
  //   renderIdAccum.toString = () => "ID аккум.";
  // }

  const [columns, setColumns] = useState<TableColumn[]>([
    // {
    //   title: "ID договора",
    //   dataIndex: "agreementId",
    //   key: "agreementId",
    //   width: "75px",
    //   render: (agreementId) => (
    //     <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{agreementId}</span>
    //   ),
    //   hidden: true,
    // },
    {
      title: renderIdTitle,
      dataIndex: "serviceId",
      key: "serviceId",
      hidden: true,
      width: "75px",
      render: (serviceId) => (
        <span style={{ color: "rgba(0, 0, 0, 0.3)", fontWeight: "normal" }}>{serviceId}</span>
      ),
    },
    {
      title: "Тип продукта",
      dataIndex: "serviceTypeName",
      key: "serviceTypeName",
      hidden: true,
      width: "180px",
    },
    {
      title: renderIdOffering,
      dataIndex: "offeringId",
      key: "offeringId",
      hidden: true,
      width: "75px",
    },
    {
      title: "Предложение",
      dataIndex: "offeringName",
      key: "offeringName",
      hidden: false,
      width: "130px",
    },
    {
      title: "Бизнес-модель",
      dataIndex: "tariffModelName",
      key: "tariffModelName",
      hidden: true,
      width: "80px",
    },
    {
      title: "Период накопления",
      dataIndex: "periodTypeName",
      key: "periodTypeName",
      hidden: true,
      width: "65px",
    },
    {
      title: "Смещение периода",
      dataIndex: "periodAlignmentName",
      key: "periodAlignmentName",
      hidden: true,
      width: "100px",
    },
    {
      title: "Период действия",
      dataIndex: "servicePeriod",
      key: "servicePeriod",
      hidden: true,
      width: "90px",
      render: ({ fromDate, toDate }) => `${fromDate} - ${toDate}`,
    },
    {
      title: "Название группы",
      dataIndex: "aggName",
      key: "aggName",
      hidden: false,
      width: "75px",
    },
    {
      title: "От (шт)",
      dataIndex: "rangeFrom",
      key: "rangeFrom",
      hidden: false,
      width: "45px",
    },
    {
      title: "До (шт)",
      dataIndex: "rangeTo",
      key: "rangeTo",
      hidden: false,
      width: "45px",
    },
    {
      title: "Контрагент",
      dataIndex: "includeLists",
      key: "includeLists",
      hidden: false,
      width: "80px",
      render: (includeLists) => (
        <>
          {includeLists.map(({ nameTitle, displayName }) => (
            <div className="account-payments__inner-cell" key={uniqueId()}>
              <span title={nameTitle}>{displayName}</span>
            </div>
          ))}
        </>
      ),
    },
    {
      title: "Тип",
      dataIndex: "documentTypeLists",
      key: "documentTypeLists",
      hidden: false,
      width: "80px",
      render: (documentTypeLists) => (
        <>
          {documentTypeLists.map(({ nameTitle, displayName }) => (
            <div className="account-payments__inner-cell" key={uniqueId()}>
              <span title={nameTitle}>{limitText(displayName, 30)}</span>
            </div>
          ))}
        </>
      ),
    },
    {
      title: "Направление",
      dataIndex: "directions",
      key: "directions",
      hidden: false,
      width: "100px",
      render: (directions) => (
        <>
          {directions.map((direction) => (
            <div className="account-payments__inner-cell" key={uniqueId()}>
              <span>{direction}</span>
            </div>
          ))}
        </>
      ),
    },
    // {
    //   title: renderIdAccum,
    //   dataIndex: "id",
    //   key: "id",
    //   hidden: true,
    //   width: "75px",
    //   render: (id) => id || "-",
    // },
    {
      title: renderIdGroup,
      dataIndex: "serviceAggRuleId",
      key: "serviceAggRuleId",
      hidden: true,
      width: "75px",
    },
    {
      title: "Период накопления",
      dataIndex: "accumulationPeriod",
      key: "accumulationPeriod",
      hidden: false,
      width: "90px",
      render: ({ periodFrom, periodTo }) => `${periodFrom} - ${periodTo}`,
    },
    {
      title: "Кол-во",
      dataIndex: "eventsCount",
      key: "eventsCount",
      hidden: false,
      width: "45px",
    },
    {
      title: "Объем",
      dataIndex: "eventsVolume",
      key: "eventsVolume",
      hidden: true,
      width: "45px",
      render: (eventsVolume) => (eventsVolume !== undefined ? Number(eventsVolume).toFixed(2) : ""),
    },
  ]);

  const onChangeAgreementId = ({
    agreementId,
    onDate,
  }: {
    agreementId?: number;
    onDate: Moment;
  }) => {
    // При изменении договора сбрасывается продукт
    searchForm.setFieldsValue({ serviceId: undefined });
    loadServices({ onDate, agreementId });
  };

  const onChangeOnDateDay = ({
    agreementId,
    serviceId,
    onDateDay,
  }: {
    agreementId?: number;
    serviceId?: number;
    onDateDay: Moment;
  }) => {
    // При выборе даты - для текущего дня время устанавливается текущее, для истекшего - конец дня
    const onDate = moment(onDateDay).isSame(moment(), "day")
      ? moment()
      : moment(onDateDay).endOf("day");
    searchForm.setFieldsValue({ onDate });
    if (!serviceId) {
      // Загрузка продуктов, если продукт не выбран. Иначе выбор даты происходит в рамках уже выбранного продукта
      loadServices({ onDate, agreementId });
    }
  };

  const onChangeOnDate = ({
    agreementId,
    serviceId,
    onDate,
  }: {
    agreementId?: number;
    serviceId?: number;
    onDate: Moment;
  }) => {
    if (!serviceId) {
      // Загрузка продуктов, если продукт не выбран. Иначе выбор даты происходит в рамках уже выбранной продукта
      loadServices({ onDate, agreementId });
    }
  };

  const onChangeFieldValue: OnChangeFieldValue = (field, allFields) => {
    // console.log(allFields);
    const { agreementId, serviceId, onDateDay, onDate } = allFields;
    const [fieldName] = Object.keys(field);
    switch (fieldName) {
      case "agreementId":
        onChangeAgreementId({ agreementId, onDate });
        break;
      case "onDateDay":
        onChangeOnDateDay({ agreementId, serviceId, onDateDay });
        break;
      case "onDate":
        onChangeOnDate({ agreementId, serviceId, onDate });
        break;
      default:
        break;
    }
  };

  const onFinish = (values) => {
    getAccumulators({ onDate: toHttpFormat(values.onDate), serviceId: values.serviceId });
  };

  const renderSearch = () => (
    <Form form={searchForm} onFinish={onFinish} onValuesChange={onChangeFieldValue}>
      <Row>
        <Col span={14}>
          <Row gutter={15}>
            <Col span={11}>
              <Form.Item name="agreementId" label="Номер договора">
                <Select
                  placeholder="Выбрать"
                  allowClear
                  loading={agreements.length === 0}
                  style={{ width: "100%" }}
                >
                  {agreements.map((agreement) => (
                    <Select.Option key={agreement.id} value={agreement.id}>
                      {agreement.agreementNumber}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={13}>
              <Form.Item name="serviceId" label="Продукт">
                <Select
                  style={{ width: "100%" }}
                  allowClear
                  placeholder="Выбрать"
                  loading={checkIsLoading(serviceLoading)}
                  filterOption={(input, option) => {
                    if (!option || !option.name) return false;
                    const lowerCaseInput = input.toLowerCase();
                    const lowerCaseName = option.name.toLowerCase();
                    return lowerCaseName.includes(lowerCaseInput);
                  }}
                  showSearch
                >
                  {transactionalServices.map((item) => {
                    const name = `${item.id} - ${item?.serviceType?.name}${
                      item?.offering?.extName ? ` - ${item?.offering?.extName}` : ""
                    }`;

                    return (
                      <Select.Option key={item.id} value={item.id} name={item.serviceType?.name}>
                        <Tooltip
                          overlayInnerStyle={{
                            display: "inline-block",
                            whiteSpace: "nowrap",
                            padding: "0",
                          }}
                          title={getServiceTooltipTitle(item)}
                        >
                          {name}
                        </Tooltip>
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col offset={1} span={9}>
          <Row gutter={15}>
            <Col span={8}>
              <Form.Item dependencies={["serviceId"]} className="form-item-dependencies">
                {({ getFieldValue }) => {
                  const serviceId = getFieldValue("serviceId");
                  const dateCreateAccount = accountVersions.reduce(
                    (result, accountVersion) =>
                      moment(accountVersion.fromDate).isBefore(result)
                        ? accountVersion.fromDate
                        : result,
                    accountVersions[0]?.fromDate
                  );
                  const disabledDate = (date) => {
                    if (date.isAfter(moment(), "day")) return true;
                    if (date.isBefore(dateCreateAccount, "day")) return true;
                    if (serviceId) {
                      const selectedService = transactionalServices.find(
                        ({ id }) => id === serviceId
                      );
                      return (
                        date.isBefore(selectedService.fromDate) ||
                        date.isAfter(selectedService.toDate)
                      );
                    }
                    return false;
                  };
                  return (
                    <Form.Item name="onDateDay" initialValue={moment()} label="Дата">
                      <DatePicker
                        allowClear={false}
                        format={displayFormat}
                        disabledDate={disabledDate}
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="onDate" initialValue={moment()} label="Время">
                <TimePicker allowClear={false} />
              </Form.Item>
            </Col>
            <Col offset={1} span={7}>
              <Button
                type="primary"
                htmlType="submit"
                style={{ width: "130px" }}
                loading={checkIsLoading(serviceLoading, accumulatorsLoading)}
              >
                Применить
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
    </Form>
  );

  const handleExportAccumulators = () => {
    if (!isEmpty(tableData)) {
      handleExport({ data: tableData, toDate: searchForm.getFieldValue("onDate"), columns });
    }
  };

  return (
    <div className="account-accumulators">
      <h1>Группы трафика</h1>
      {renderSearch()}
      <Divider style={{ margin: "0" }} />
      <div style={{ marginLeft: "97%", marginTop: "7px" }}>
        <ToggleColumns columns={columns} setColumns={setColumns} tableName="accumulators" />
      </div>
      <Table
        size="small"
        className="table_no-hover"
        columns={[...columns].filter((item) => !item.hidden)}
        dataSource={tableData}
        loading={checkIsLoading(accumulatorsLoading)}
        pagination={false}
        scroll={{ y: 400, x: 1200 }}
        rowClassName={({ sequenceNumber }) =>
          sequenceNumber % 2 ? "account-accumulators__oddRow" : ""
        }
      />
      <Divider />
      {hasAccess(MENU_ACCOUNT_AGG_USAGE_WRITE) && (
        <Button
          type="primary"
          disabled={tableData?.length === 0}
          loading={checkIsLoading(exportToXLSLoading)}
          onClick={handleExportAccumulators}
          style={{ display: "block", marginLeft: "auto", marginRight: "auto" }}
        >
          Выгрузить в XLS
        </Button>
      )}
    </div>
  );
};

export default AccountAccumulators;
